import { z } from 'zod';

/**
 * Cards kept on an organization's Stripe customer. Stripe is the source of truth; `org_payment_methods` is a
 * mirror the worker refreshes after every change, so the app can list the cards and know the default without
 * calling Stripe on every page. Nothing here is card data: a brand, four digits and an expiry, which is what
 * Stripe itself shows. The Stripe payment method id is stored (charging needs it) but is not in this schema:
 * only the payment route hands one out, in `defaultPaymentMethodSchema`.
 */
export const paymentMethodSchema = z.object({
  id: z.uuid(),
  organization_id: z.uuid(),
  brand: z.string().min(1).max(40),
  last4: z.string().regex(/^[0-9]{4}$/),
  exp_month: z.number().int().min(1).max(12),
  exp_year: z.number().int().min(2000).max(2100),
  is_default: z.boolean(),
  /** Null when the card was saved at Stripe rather than through the app, or the person has since been deleted. */
  added_by: z.uuid().nullable(),
  created_at: z.iso.datetime({ offset: true }),
});
export type PaymentMethod = z.infer<typeof paymentMethodSchema>;

/** `POST /api/orgs/:orgId/payment-methods/sync`: the organization's cards as Stripe has them right now. */
export const paymentMethodListResponseSchema = z.object({
  paymentMethods: z.array(paymentMethodSchema),
});
export type PaymentMethodListResponse = z.infer<typeof paymentMethodListResponseSchema>;

/** `POST /api/orgs/:orgId/payment-methods/setup`: the client secret the add-a-card form is mounted with. */
export const setupPaymentMethodResponseSchema = z.object({ clientSecret: z.string().min(1) });
export type SetupPaymentMethodResponse = z.infer<typeof setupPaymentMethodResponseSchema>;

/**
 * The organization's default card as the payment route names it: the Stripe payment method id a one-step submit
 * confirms with, and just enough to say which card it is. Never more than these three values.
 */
export const defaultPaymentMethodSchema = z.object({
  id: z.string().min(1),
  brand: z.string().min(1).max(40),
  last4: z.string().regex(/^[0-9]{4}$/),
});
export type DefaultPaymentMethod = z.infer<typeof defaultPaymentMethodSchema>;

/**
 * The address a one-step confirmation applies to its session so tax can be worked out before anyone confirms.
 * It is the organization's own billing address; the payment page collects one in its form instead.
 */
export const paymentBillingAddressSchema = z.object({
  name: z.string().min(1),
  line1: z.string().min(1),
  line2: z.string().nullable(),
  city: z.string().min(1),
  region: z.string().nullable(),
  postalCode: z.string().min(1),
  country: z.string().length(2),
});
export type PaymentBillingAddress = z.infer<typeof paymentBillingAddressSchema>;

/** Stripe's card brands, in our words. Anything new from Stripe reads as a plain "Card". */
const BRANDS: Readonly<Record<string, string>> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  american_express: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  diners_club: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
  union_pay: 'UnionPay',
  cartes_bancaires: 'Cartes Bancaires',
  interac: 'Interac',
  eftpos_australia: 'Eftpos Australia',
};

/** "Visa ending 4242": the one wording for a card, in settings, in the submit dialog and in email. */
export function describeCard(card: { brand: string; last4: string }): string {
  return `${BRANDS[card.brand.toLowerCase()] ?? 'Card'} ending ${card.last4}`;
}
